import { SheetContentId, SheetContentLoadedEvent } from './CouponsAndVouchersSheet';
import { Action } from '@otto-ec/tracking-bct';
import { reloadVouchersSheet } from './reloadVouchersSheet';
import { Reloader } from '../basket-checkout-common/Reloader';
import { Tracking } from '../shared/Tracking';
import { onSheetOpen } from './SheetOpenEvents';

export class Vouchers {
    static formSelector = '.or_js_voucherSheetRedemptionForm';
    static inputFieldSelector = '.or_js_voucherSheetInput';
    static buttonSelector = '.or_js_voucherSheetRedeemButton';
    static deleteButtonSelector = '.or_js_voucher_remove';
    static addVoucherButtonSelector = '.or_js_voucherSheetAddVoucherButton';

    static VOUCHER_NOTE_INVALID_PATTERN = 'Der eingegebene Code ist leider nicht gültig. Bitte prüfe deine Eingabe und die Vorgaben zur Einlösung.';
    static VOUCHER_NOTE_UNKNOWN_OR_DENIED = 'Der eingegebene Code ist leider nicht gültig. Bitte prüfe deine Eingabe.';
    static VOUCHER_NOTE_ALREADY_USED = 'Der Gutscheincode wurde bereits eingegeben.';
    static VOUCHER_NOTE_NO_AVAILABLE_VALUE = 'Der eingegebene Gutscheincode wurde bereits vollständig eingelöst.';
    static VOUCHER_NOTE_EXPIRED = 'Der eingegebene Gutscheincode ist bereits abgelaufen.';
    static VOUCHER_NOTE_TECHNICAL =
        'Aufgrund eines technischen Problems konnte der eingegebene Code nicht eingelöst werden. Bitte versuche es erneut.';
    static VOUCHER_NOTE_LOGGED_OUT = 'Aus Sicherheitsgründen haben wir dich abgemeldet. Bitte melde dich neu an um Gutscheine einzulösen.';

    constructor(
        private readonly reloader: Reloader,
        private readonly tracking: Tracking
    ) {}

    initialize() {
        this._subscribeToContentLoadedEvent();
    }

    private _subscribeToContentLoadedEvent() {
        onSheetOpen((event: SheetContentLoadedEvent) => {
            if (event.id === SheetContentId.VOUCHERS) {
                this._initialize();
            }
        });
    }

    _initialize = () => {
        const formElement: HTMLFormElement | null = document.querySelector<HTMLFormElement>(Vouchers.formSelector);

        if (!formElement) {
            return;
        }

        const voucherRedemptionInput: HTMLInputElement | null = document.querySelector<HTMLInputElement>(Vouchers.inputFieldSelector);
        if (!voucherRedemptionInput) {
            return;
        }

        const voucherRedemptionButton: HTMLButtonElement | null = document.querySelector<HTMLButtonElement>(Vouchers.buttonSelector);
        if (!voucherRedemptionButton) {
            return;
        }

        voucherRedemptionInput.addEventListener('input', () => {
            if (!voucherRedemptionInput.value) {
                this._resetRedemptionHints(voucherRedemptionInput);
            }
        });
        formElement.addEventListener('submit', e => this._handleSubmitEvent(e, voucherRedemptionInput, voucherRedemptionButton));

        document
            .querySelectorAll<HTMLButtonElement>(Vouchers.deleteButtonSelector)
            .forEach(b => b.addEventListener('click', e => this._handleDeleteButtonClicked(e)));

        const addVoucherButton: HTMLButtonElement | null = document.querySelector<HTMLButtonElement>(Vouchers.addVoucherButtonSelector);
        if (addVoucherButton) {
            addVoucherButton.addEventListener('click', () => this._handleAddVoucherButtonClicked());
        }
    };

    private _handleAddVoucherButtonClicked() {
        const addVoucherButtonDiv: HTMLButtonElement | null = document.querySelector<HTMLButtonElement>(Vouchers.addVoucherButtonSelector);
        const formElement: HTMLFormElement | null = document.querySelector<HTMLFormElement>(Vouchers.formSelector);
        const addVoucherButton = addVoucherButtonDiv?.querySelector('oc-button-v1');

        if (addVoucherButton?.hasAttribute('disabled')) {
            return;
        }

        if (addVoucherButtonDiv) {
            addVoucherButtonDiv.style.display = 'none';
        }

        if (formElement) {
            formElement.style.display = '';
        }
    }

    private async _handleDeleteButtonClicked(e: MouseEvent) {
        const deleteButton = e.target as HTMLButtonElement | null;
        const parentReductionItem = deleteButton?.closest('.or_js_papercard') as HTMLElement | null;
        const topContent = parentReductionItem?.querySelector('.or_js_priceReductionItem') as HTMLElement | null;
        const voucherId = topContent?.dataset.reductionId;
        const usageStatus = topContent?.dataset.reductionUsageStatus;
        const usageInfo = topContent?.dataset.reductionUsageInfo;
        const savings = topContent?.dataset.reductionSavings;
        const marketplaceVoucherId = topContent?.dataset.reductionMarketplaceVoucherId;
        const isPlatformVoucher = topContent?.dataset.reductionIsPlatformVoucher === 'true';

        if (!voucherId || !usageStatus || !usageInfo || !savings) {
            return;
        }

        const htmlButton = deleteButton?.closest('button');
        try {
            htmlButton?.setAttribute('disabled', 'disabled');
            const response: Response = await this._deleteVoucherRedemption(voucherId);
            if (response.ok) {
                await this._handleDeletionSuccess(usageStatus, usageInfo, savings, isPlatformVoucher, marketplaceVoucherId);
            } else {
                this._handleDeletionFailure(response, htmlButton);
            }
        } catch (err) {
            this._handleDeletionFailure(err, htmlButton);
        }
    }

    private async _handleSubmitEvent(e: SubmitEvent, input: HTMLInputElement, button: HTMLButtonElement) {
        e.preventDefault();
        const code = input.value;
        if (!code) {
            return;
        }

        this._toLoadingState(input, button);
        try {
            const response: Response = await this._redeemCode(code.trim().toUpperCase());
            if (response.ok) {
                await this._handleRedemptionSuccess(response, input);
            } else {
                await this._handleRedemptionFailure(response, input);
            }
        } catch (err) {
            console.error('redemption failed', err);
            await this._handleRedemptionFailure(null, input).catch(console.error);
        } finally {
            this._toDefaultState(input, button);
        }
    }

    private _toLoadingState(input: HTMLElement | undefined, button: HTMLElement) {
        input?.setAttribute('disabled', '');
        button.setAttribute('disabled', '');
        button.setAttribute('loading', '');
    }

    private _toDefaultState(input: HTMLElement | undefined, button: HTMLElement) {
        input?.removeAttribute('disabled');
        button.removeAttribute('disabled');
        button.removeAttribute('loading');
    }

    private async _handleRedemptionSuccess(response: Response, input: HTMLInputElement) {
        this._resetRedemptionHints(input);

        try {
            const body = await response.json();

            await this.reloader.trigger();

            reloadVouchersSheet();
            const voucherResponse = body as VoucherSuccessResponse;
            this._trackVoucherSubmitSuccess(voucherResponse);
        } catch (e) {
            console.error(e);
            await this._handleRedemptionFailure(null, input);
        }
    }

    private async _handleRedemptionFailure(response: Response | null, input: HTMLInputElement) {
        let errorKey: string;
        switch (response?.status) {
            case 422:
                const body = (await response.json()) as ErrorResponse;
                errorKey = body.errorKey;
                break;
            case 401:
                errorKey = 'voucher.redemption.logged-out';
                break;
            default:
                errorKey = 'voucher.redemption.technical-error';
        }
        this._showErrorHint(errorKey, input);
        this._trackSubmitFailure(errorKey);
    }

    private async _handleDeletionSuccess(
        usageStatus: string,
        usageInfo: string,
        savings: string,
        isPlatformVoucher: boolean,
        marketplaceVoucherId?: string
    ) {
        await this.reloader.trigger();

        reloadVouchersSheet();
        this._trackVoucherDeletionSuccess(usageStatus, usageInfo, savings, isPlatformVoucher, marketplaceVoucherId);
    }

    private _handleDeletionFailure(err: unknown, deleteButton: HTMLButtonElement | null | undefined) {
        console.error('voucher deletion failed', err);
        deleteButton?.removeAttribute('disabled');
    }

    private _showErrorHint(errorKey: string, input: HTMLInputElement) {
        const errorKeyToHintMap: Record<string, string> = {
            'voucher.redemption.logged-out': Vouchers.VOUCHER_NOTE_LOGGED_OUT,
            'voucher.redemption.invalidPattern': Vouchers.VOUCHER_NOTE_INVALID_PATTERN,
            'voucher.redemption.expired': Vouchers.VOUCHER_NOTE_EXPIRED,
            'voucher.redemption.noAvailableValue': Vouchers.VOUCHER_NOTE_NO_AVAILABLE_VALUE,
            'voucher.redemption.alreadyUsed': Vouchers.VOUCHER_NOTE_ALREADY_USED,
            'voucher.redemption.unknown': Vouchers.VOUCHER_NOTE_UNKNOWN_OR_DENIED,
            'voucher.redemption.technical-error': Vouchers.VOUCHER_NOTE_TECHNICAL
        };

        this._resetRedemptionHints(input);

        if (errorKey in errorKeyToHintMap) {
            input.setAttribute('validation-message', errorKeyToHintMap[errorKey]);
        } else {
            input.setAttribute('validation-message', Vouchers.VOUCHER_NOTE_TECHNICAL);
            console.error('unknown error key', errorKey);
        }
    }

    private _resetRedemptionHints(input: HTMLInputElement) {
        input.removeAttribute('validation-message');
    }

    private _redeemCode(code: string): Promise<Response> {
        return fetch('/order/basket/marketplace-redemption', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ code: code }),
            redirect: 'error' /*                                          */
        });
    }

    private _deleteVoucherRedemption(voucherId: string): Promise<Response> {
        return fetch(`/order/basket/marketplace-redemption/${voucherId}`, {
            method: 'DELETE',
            redirect: 'error' /*                                          */
        });
    }

    private _trackVoucherSubmitSuccess(voucherResponse: VoucherSuccessResponse) {
        const labels: Record<string, Array<string>> = {
            order_ReductionType: ['voucher'],
            order_ReductionUsageStatus: [voucherResponse.usageStatus],
            order_ReductionSavings: [voucherResponse.savings],
            order_ReductionCreationStatus: [voucherResponse.creationStatus],
            order_ReductionMarketplaceVoucherId: [voucherResponse.marketplaceVoucherId],
            order_ReductionPlatform: [(voucherResponse.isPlatformVoucher ?? false).toString()]
        };

        this.tracking.submitEventMerge({
            name: 'submit',
            features: [
                {
                    id: 'basket_reduction_submitted',
                    name: 'BasketReduction',
                    status: 'submitted',
                    labels: labels
                }
            ]
        });
    }

    private _trackVoucherDeletionSuccess(
        usageStatus: string,
        usageInfo: string,
        savings: string,
        isPlatformVoucher: boolean,
        marketplaceVoucherId?: string
    ) {
        const labels: Record<string, Array<string>> = {
            order_ReductionType: ['voucher'],
            order_ReductionUsageStatus: [usageStatus],
            order_ReductionUsageInfo: [usageInfo],
            order_ReductionSavings: [savings],
            order_ReductionPlatform: [isPlatformVoucher.toString()]
        };
        if (marketplaceVoucherId) {
            labels.order_ReductionMarketplaceVoucherId = [marketplaceVoucherId];
        }

        this.tracking.submitEventMerge({
            name: 'delete',
            features: [
                {
                    id: 'basket_reduction_deleted',
                    name: 'BasketReduction',
                    status: 'deleted',
                    labels: labels
                }
            ]
        });
    }

    private _trackSubmitFailure(reason: string) {
        const action: Action = {
            name: 'submit',
            features: [
                {
                    id: 'basket_reduction_submitted',
                    name: 'BasketReduction',
                    status: 'error',
                    labels: {
                        order_ReductionType: ['voucher']
                    }
                },
                {
                    id: 'order_message',
                    name: 'OrderMessage',
                    status: 'visible',
                    labels: {
                        order_ErrorMessage: [reason]
                    }
                }
            ]
        };
        this.tracking.submitEventMerge(action, true);
    }
}

export interface VoucherSuccessResponse {
    usageStatus: string;
    savings: string;
    creationStatus: CreationStatus;
    marketplaceVoucherId: string;
    isPlatformVoucher: boolean;
}

export interface ErrorResponse {
    errorKey: string;
}

export type CreationStatus = 'old_voucher' | 'new_voucher';
